import React, { useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";
import MainWrapper from "../../MainWrapper";
import "../../../assets/css/Main.css";
import { connect } from "react-redux";
import Loader from "../../layouts/Loader";
import { toast } from "react-toastify";
import { USER_MENU_LIST_REQUEST } from "../../../_utils/constants/admin/Admin";
import { WIDGET_COMPONENTS } from "./widgets";
import "./Dashboard.css";

// Preferred on-screen arrangement. Each inner array is a horizontal row; only
// the role's selected + registered widgets render, rows with no selected widgets
// are skipped, and a row with multiple ids lays them out side by side (centered).
// Any selected widget not listed here is appended as its own row so new widgets
// are never dropped.
const DASHBOARD_LAYOUT = [
  ["potential-lost-customers", "estimate-ready", "ar-over-90", "inventory-over-90"],
  ["tiered-customer-performance"],
  ["non-tiered-customer-performance"],
  ["new-customers-secured", "estimating-turnaround"],
  ["revenue-profit"],
  ["insufficient-materials"],
];

// Widgets that should render full page width, left-aligned (e.g. wide data
// tables) instead of being centered like the cards/KPI widgets.
const FULL_WIDTH_WIDGETS = new Set(["insufficient-materials"]);

// Role-driven dashboard orchestrator.
//
// The set of widgets a user sees is configured per Role (in the Role admin
// modal's "Dashboard" section) and stored in MS_Role.PermissionObject. We read
// that config via the same role-permission fetch the Sidebar uses
// (USER_MENU_LIST_REQUEST -> Admin.userMenuList[0]), then render the selected
// widgets from the widget registry.
//
// Each widget is passed `dataScope` ("all" | "self") and `userId` so it can
// optionally filter its data to the logged-in user (used by the Sales role).
const Dashboard = ({
  auth: { auth, isAuthenticated },
  Admin: { userMenuList, isLoading },
  fetchUserMenuList,
}) => {
  useEffect(() => {
    // One-time login-success toast (preserved from the original dashboard).
    let isLoggedIn = JSON.parse(localStorage.getItem("isLoggedIn"));
    if (isAuthenticated && isLoggedIn) {
      toast.success("Login success.");
      localStorage.setItem("isLoggedIn", false);
    }
    if (auth[0]?.token && auth[0]?.roleid && auth[0]?.userId) {
      fetchUserMenuList(auth[0].token, auth[0].roleid, auth[0].userId);
    }
  }, [auth, isAuthenticated]);

  // Resolve this role's dashboard config from the role-permission payload.
  const { widgetIds, dataScope } = useMemo(() => {
    const dashboard =
      userMenuList && userMenuList[0] ? userMenuList[0].dashboard : null;
    const configured =
      dashboard && Array.isArray(dashboard.widgets) ? dashboard.widgets : [];

    return {
      // The role's checked widgets are authoritative: render exactly the widgets
      // selected in the role's Dashboard config. None selected => empty dashboard.
      widgetIds: configured,
      dataScope: (dashboard && dashboard.dataScope) || "all",
    };
  }, [userMenuList]);

  if (!isAuthenticated) return <Navigate to="/login" />;

  const userId = auth[0]?.userId;

  // Arrange the role's selected widgets into the preferred layout rows; drop
  // empty rows and append any selected-but-unlisted widgets as their own rows.
  const selected = widgetIds.filter((id) => WIDGET_COMPONENTS[id]);
  const rows = DASHBOARD_LAYOUT
    .map((row) => row.filter((id) => selected.includes(id)))
    .filter((row) => row.length > 0);
  const placed = new Set(rows.flat());
  selected.filter((id) => !placed.has(id)).forEach((id) => rows.push([id]));
  return (
    <MainWrapper>
      <Loader loading={isLoading}>
        <div className="dashboard-grid">
          {rows.map((row, i) => (
            <div className="dashboard-row" key={i}>
              {row.map((id) => {
                const Widget = WIDGET_COMPONENTS[id];
                const cellClass = FULL_WIDTH_WIDGETS.has(id)
                  ? "dashboard-cell dashboard-cell--full"
                  : "dashboard-cell";
                return (
                  <div className={cellClass} key={id}>
                    <Widget dataScope={dataScope} userId={userId} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </Loader>
    </MainWrapper>
  );
};

const mapStateToProps = ({ auth, Admin }) => {
  return {
    auth,
    Admin,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchUserMenuList: (token, roleId, userId) =>
      dispatch({ type: USER_MENU_LIST_REQUEST, token, roleId, userId }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
